import React from 'react';
import {
    Box, Typography, Paper, FormControl, InputLabel, Select, MenuItem,
    TextField, Divider, Stack, Grid
} from '@mui/material';

// G4: Kalem (parça/ürün) fiyatı yeni kayıtlarda YOK. Ürün seçimi fiyatsızdır;
// kategori başına tek opsiyonel "Tutar" alanı (kategoriTutar/onKategoriTutarChange
// prop'ları verilirse) girilir. Eski kayıtlardaki kalem fiyatları salt-okunur
// gösterilir, silinmez (çift-format).
// G2: "Paket Satışı" toggle'ı ve paket seçim dalları emekli edildi (2026-08-29).
// Eski paket-formatlı state dizileri (satisTipi==='paket') bu bileşende
// DEĞİŞTİRİLMEDİĞİ sürece aynen geri yazılır — veri kaybı yok.
const PartSelectionSection = ({ title, parts, services, data, setData, kategoriTutar, onKategoriTutarChange }) => {
    const tekilHizmetler = services.filter(s => !s.isPaket);

    const handlePartChange = (part, field, value) => {
        const existingPartIndex = data.findIndex(p => p.ad === part);
        let newData = [...data];

        if (existingPartIndex > -1) {
            if (!value && field === "urunId") {
                newData.splice(existingPartIndex, 1);
            } else {
                newData[existingPartIndex][field] = value;
                if (field === "urunId") {
                    const selectedService = services.find(s => s.id === value);
                    if (selectedService) {
                        newData[existingPartIndex]['urunAdi'] = selectedService.hizmetAdi;
                        // Ürün değişti: eski kalem fiyatı/paket içeriği artık bu ürüne ait değil.
                        delete newData[existingPartIndex]['fiyat'];
                        delete newData[existingPartIndex]['icindekiHizmetler'];
                    }
                }
            }
        } else {
            if (field === "urunId" && value) {
                const selectedService = services.find(s => s.id === value);
                if (selectedService) {
                    newData.push({ ad: part, urunId: value, urunAdi: selectedService.hizmetAdi || '' });
                }
            }
        }
        setData(newData);
    };

    // Eski paket-formatlı kayıt (satisTipi==='paket'): seçim değiştirilemez, yalnız bilgi gösterilir.
    const eskiPaketKaydi = data.length > 0 && data[0]?.satisTipi === 'paket' ? data[0] : null;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{title}</Typography>
            <Divider sx={{ my: 1 }} />

            {eskiPaketKaydi ? (
                <Box sx={{ mt: 2, pl: 2, borderLeft: 3, borderColor: 'warning.main', p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Eski kayıt — Paket: {eskiPaketKaydi.paketAdi}
                        {Number(eskiPaketKaydi.paketFiyat || eskiPaketKaydi.fiyat) > 0 &&
                            ` (${Number(eskiPaketKaydi.paketFiyat || eskiPaketKaydi.fiyat).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Paket satışı yeni girişten kaldırıldı; bu kayıt aynen korunur, kaydettiğinizde değişmez.
                    </Typography>
                    <Stack spacing={0.5}>
                        {(eskiPaketKaydi.parcalar || []).filter(p => p.urunId).map((parca, idx) => (
                            <Typography key={idx} variant="body2" color="text.secondary">
                                • {parca.ad}: {parca.urunAdi}
                            </Typography>
                        ))}
                    </Stack>
                </Box>
            ) : (
                <Stack spacing={2} sx={{ mt: 2 }}>
                    {parts.map(part => {
                        const currentPartData = data.find(p => p.ad === part);
                        // Eski kayıttan gelen kalem fiyatı: değiştirilemez, yalnız gösterilir.
                        const eskiKalemFiyat = currentPartData && typeof currentPartData.fiyat === 'number' && currentPartData.fiyat > 0
                            ? currentPartData.fiyat : null;

                        return (
                            <Box key={part}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={4} md={3}>
                                        <Typography>{part}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={eskiKalemFiyat !== null ? 5 : 8} md={eskiKalemFiyat !== null ? 6 : 9}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Ürün/Hizmet</InputLabel>
                                            <Select
                                                value={currentPartData?.urunId || ''}
                                                label="Ürün/Hizmet"
                                                onChange={(e) => handlePartChange(part, 'urunId', e.target.value)}
                                            >
                                                {tekilHizmetler.map(s => <MenuItem key={s.id} value={s.id}>{s.hizmetAdi}</MenuItem>)}
                                                <Divider />
                                                <MenuItem value=""><em>- Seçimi Kaldır -</em></MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    {eskiKalemFiyat !== null && (
                                        <Grid item xs={12} sm={3} md={3}>
                                            <Typography variant="body2" color="text.secondary" align="right">
                                                Eski kayıt fiyatı: {eskiKalemFiyat.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        );
                    })}
                </Stack>
            )}

            {onKategoriTutarChange && (
                <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <TextField
                        label="Kategori Tutarı (₺) — opsiyonel"
                        size="small"
                        type="number"
                        value={kategoriTutar ?? ''}
                        onChange={(e) => onKategoriTutarChange(e.target.value)}
                        helperText="Bu kategorideki işlerin toplamı. Girilirse alttaki Toplam Tutar önerisine eklenir."
                        sx={{ maxWidth: 320 }}
                        fullWidth
                    />
                </Box>
            )}
        </Paper>
    );
};

export default PartSelectionSection;
